import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccount, useChainId, useSwitchChain, useSignMessage } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { api } from '../api/client';
import { referralApi } from '../api/referral';

const POLYGON = 137;
const usd = (n?: number) => '$' + (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const shortAddr = (a?: string) => (a ? a.slice(0, 6) + '…' + a.slice(-4) : '');
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-3xl ${className}`} style={{ backgroundColor: '#15122e', border: '1px solid rgba(255,255,255,0.06)' }}>{children}</div>;
}

export default function Payouts() {
  const qc = useQueryClient();
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { signMessageAsync } = useSignMessage();
  const { openConnectModal } = useConnectModal();

  const walletQ = useQuery({ queryKey: ['my-wallet'], queryFn: () => api<{ wallet: { address: string; chainId: number; linkedAt: string } | null }>('/api/wallet') });
  const meQ = useQuery({ queryKey: ['referral-me'], queryFn: referralApi.me });
  const payoutsQ = useQuery({ queryKey: ['referral-payouts'], queryFn: referralApi.payouts });

  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const linked = walletQ.data?.wallet ?? null;
  const onPolygon = chainId === POLYGON;

  async function linkWallet() {
    setErr(null);
    if (!isConnected || !address) { openConnectModal?.(); return; }
    setBusy(true);
    try {
      // 1) FORCE POLYGON before any signing — the non-negotiable step.
      if (chainId !== POLYGON) {
        setStep('Switching to Polygon…');
        await switchChainAsync({ chainId: POLYGON });
      }
      // 2) Ask the backend for a SIWE challenge.
      setStep('Preparing signature…');
      const ch = await api<{ challengeId: string; message: string; expiresAt: string }>('/api/wallet/challenge', {
        method: 'POST', body: { walletAddress: address },
      });
      // 3) Sign the exact message the server gave us.
      setStep('Waiting for wallet signature…');
      const signature = await signMessageAsync({ message: ch.message });
      // 4) Submit to link.
      setStep('Linking wallet…');
      await api('/api/wallet/link', { method: 'POST', body: { challengeId: ch.challengeId, signature } });
      // 5) Refresh everything the link affects (backfill may credit earlier buys).
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['my-wallet'] }),
        qc.invalidateQueries({ queryKey: ['referral-me'] }),
        qc.invalidateQueries({ queryKey: ['referral-payouts'] }),
      ]);
      setStep(null);
    } catch (e: any) {
      setErr(e?.shortMessage || e?.message || 'Could not link wallet. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const payable = meQ.data?.earnings?.payableUsdt;
  const paid = payoutsQ.data?.totalPaidUsdt;
  const history = payoutsQ.data?.payouts ?? [];

  return (
    <div className="space-y-5 pb-2">
      <div>
        <div className="text-[11px] tracking-widest" style={{ color: '#8B82A8' }}>WALLET</div>
        <h1 className="text-3xl font-bold leading-tight" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Payouts</h1>
      </div>

      {!linked ? (
        <Card className="p-5">
          <div className="font-semibold mb-1" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Connect a payout wallet</div>
          <p className="text-sm mb-4" style={{ color: '#8B82A8' }}>Link a Polygon wallet to receive your USDT commissions. You’ll sign a quick message — no transaction, no fee.</p>

          {isConnected && (
            <div className="rounded-2xl p-3 mb-3 text-sm" style={{ backgroundColor: '#1d1640' }}>
              <span style={{ color: '#8B82A8' }}>Connected: </span>
              <span style={{ fontFamily: '"JetBrains Mono", monospace', color: '#c4b5fd' }}>{shortAddr(address)}</span>
              {!onPolygon && <div className="text-xs mt-1" style={{ color: '#FBBF24' }}>Not on Polygon — we’ll switch it for you when you link.</div>}
            </div>
          )}

          {err && <div className="rounded-2xl px-4 py-3 mb-3 text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#F87171' }}>{err}</div>}
          {busy && step && <div className="rounded-2xl px-4 py-3 mb-3 text-sm" style={{ backgroundColor: 'rgba(139,92,246,0.12)', color: '#c4b5fd' }}>{step}</div>}

          <button onClick={linkWallet} disabled={busy} className="w-full rounded-2xl py-3.5 font-semibold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Working…' : isConnected ? 'Sign to link wallet' : 'Connect wallet'}
          </button>
        </Card>
      ) : (
        <>
          <Card className="p-5">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-[11px] tracking-widest" style={{ color: '#8B82A8' }}>LINKED WALLET</div>
                <div className="text-sm mt-1" style={{ fontFamily: '"JetBrains Mono", monospace', color: '#c4b5fd' }}>{shortAddr(linked.address)}</div>
              </div>
              <span className="text-[11px] px-2 py-1 rounded-full font-semibold" style={{ backgroundColor: 'rgba(52,211,153,0.15)', color: '#34D399' }}>Polygon ✓</span>
            </div>
          </Card>

          <div className="flex gap-3">
            <Card className="flex-1 p-4">
              <div className="text-2xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif', color: '#34D399' }}>{usd(payable)}</div>
              <div className="text-xs mt-1" style={{ color: '#8B82A8' }}>Payable now</div>
            </Card>
            <Card className="flex-1 p-4">
              <div className="text-2xl font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif', color: '#c4b5fd' }}>{usd(paid)}</div>
              <div className="text-xs mt-1" style={{ color: '#8B82A8' }}>Paid to date</div>
            </Card>
          </div>

          <Card className="p-4">
            <p className="text-xs" style={{ color: '#8B82A8' }}>Payouts run weekly and are sent automatically once your KYC is approved and a wallet is linked.</p>
          </Card>

          <div>
            <div className="font-semibold mb-3" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Payout history</div>
            {history.length === 0 ? (
              <Card className="p-5"><div className="text-sm" style={{ color: '#8B82A8' }}>No payouts yet. Your first weekly payout will appear here.</div></Card>
            ) : (
              <div className="space-y-3">
                {history.map((p) => (
                  <Card key={p.id} className="p-4 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold" style={{ color: '#34D399' }}>{usd(p.usdtAmount)}</div>
                      <div className="text-[11px]" style={{ color: '#8B82A8' }}>{fmtDate(p.createdAt)} · {p.commissionCount} commission{p.commissionCount === 1 ? '' : 's'}</div>
                    </div>
                    {p.txHash && (
                      <button onClick={() => window.open(`https://polygonscan.com/tx/${p.txHash}`, '_blank')} className="text-xs font-semibold" style={{ color: '#a78bfa' }}>View tx ›</button>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}